import {useCallback, useEffect, useRef, useState} from 'react';

const SESSION_DURATION =
  import.meta.env.VITE_LONG_DEV_SESSION === 'true'
    ? 60 * 60 * 1000
    : 5 * 60 * 1000;
const CHECK_INTERVAL = 1000; // 1 second
const EXPIRE_ALERT_TIME = 60 * 1000; // 1 minute
const THROTTLE_DELAY = 1000; // Only update state once every 1000ms for mouse moves

export const useActivityMonitor = () => {
  const [activityTime, setActivityTime] = useState<number>(Date.now());
  const [currentTime, setCurrentTime] = useState<number>(Date.now());

  // Derive expireTime during the render cycle
  const expireTime = activityTime + SESSION_DURATION;
  const expiresIn = expireTime - currentTime;
  const isExpired = currentTime >= expireTime;
  const isAlmostExpired = expiresIn <= EXPIRE_ALERT_TIME;

  const lastUpdateRef = useRef<number>(Date.now());

  const handleAction = useCallback(() => {
    const now = Date.now();

    // Throttle updates to avoid excessive state changes
    if (now - lastUpdateRef.current < THROTTLE_DELAY) {
      return;
    }

    setActivityTime(now);
    lastUpdateRef.current = now;
  }, []);

  useEffect(() => {
    // Update currentTime every 1 second
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, CHECK_INTERVAL);

    // Add event listeners for user activity
    const events = [
      'keydown',
      'mousedown',
      'click',
      'popstate',
      'zendeskMessageSent',
    ];
    events.forEach((event) => window.addEventListener(event, handleAction));

    return () => {
      clearInterval(timer);
      events.forEach((event) =>
        window.removeEventListener(event, handleAction),
      );
    };
  }, [handleAction]);

  return {
    currentTime,
    activityTime,
    expireTime,
    expiresIn,
    isExpired,
    isAlmostExpired,
  };
};
